import React from "react";
import { motion } from "framer-motion";
import { 
  ChefHat, 
  Utensils, 
  Wallet, 
  UserCheck, 
  ArrowRight, 
  Bike, 
  MapPin,
  Star
} from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/index";
import { menuItems, restaurantInfo, locationInfo } from "@/data/index";
import { MenuCard, FeatureCard, LocationCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true }
};

export default function Home() {
  const bestSellers = menuItems.filter(item => item.isBestSeller).slice(0, 3);

  const featureIcons = [
    <Utensils className="w-6 h-6" />,
    <ChefHat className="w-6 h-6" />,
    <Wallet className="w-6 h-6" />,
    <UserCheck className="w-6 h-6" />
  ];

  return (
    <div className="flex flex-col gap-0">
      {/* Hero Section */}
      <section 
        id="hero" 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.MAIN_31} 
            alt="Signature Dakgalbi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-left"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6">
              <Star className="w-4 h-4 fill-primary" />
              Authentic Korean Dakgalbi Specialist
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6">
              Savor the <span className="text-primary">True Heart</span> of Seoul
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-lg">
              Experience the authentic taste of premium stir-fried chicken Dakgalbi and crispy Korean Fried Chicken, served with passion in the heart of Sunway Geo Mall.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8 shadow-lg shadow-primary/20">
                <Link to={ROUTE_PATHS.MENU}>View Full Menu</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 border-primary/20 hover:bg-primary/5">
                <a href="#location">Find Us</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            {...fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Bagus Dakgalbi?</h2>
            <p className="text-muted-foreground">We combine traditional Korean culinary arts with a modern, friendly atmosphere to bring you a dining experience that feels like home.</p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {restaurantInfo.values.map((value, index) => (
              <motion.div key={value.title} variants={fadeInUp}>
                <FeatureCard 
                  title={value.title}
                  description={value.description}
                  icon={featureIcons[index]}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Menu Highlights Section */}
      <section id="menu" className="py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Signature Best Sellers</h2>
              <p className="text-muted-foreground">Hand-picked favorites that our customers keep coming back for.</p>
            </motion.div>
            <motion.div {...fadeInUp}>
              <Button asChild variant="link" className="text-primary font-semibold text-lg p-0 h-auto flex items-center gap-2 group">
                <Link to={ROUTE_PATHS.MENU}>
                  Explore Entire Menu
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {bestSellers.map((item) => (
              <motion.div key={item.id} variants={fadeInUp}>
                <MenuCard item={item} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Delivery Focus */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="relative rounded-[2rem] overflow-hidden bg-primary px-8 py-16 md:p-20 text-center">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <img src={IMAGES.KOREAN_CHICKEN_1} alt="Pattern" className="w-full h-full object-cover" />
            </div>
            <motion.div 
              {...fadeInUp}
              className="relative z-10 max-w-2xl mx-auto"
            >
              <Bike className="w-12 h-12 text-primary-foreground mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl font-bold text-primary-foreground mb-6">
                Craving Korean Food at Home?
              </h2>
              <p className="text-primary-foreground/80 text-lg mb-10">
                We're on Grab Delivery! Get your favorite Dakgalbi and Crispy Fried Chicken delivered hot to your doorstep anywhere in Bandar Sunway.
              </p>
              <Button 
                asChild 
                size="lg" 
                variant="secondary" 
                className="rounded-full px-10 h-14 text-lg font-bold shadow-xl"
              >
                <a href={locationInfo.grabLink} target="_blank" rel="noopener noreferrer">
                  Order on Grab Now
                </a>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-24 bg-muted/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp} className="order-2 lg:order-1">
              <LocationCard location={locationInfo} />
            </motion.div>
            
            <motion.div 
              {...fadeInUp} 
              className="order-1 lg:order-2 space-y-8"
            >
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.RESTAURANT_INTERIOR_2} 
                  alt="Bagus Chicken Interior"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 p-6 rounded-2xl bg-background/80 backdrop-blur-md border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-primary/20 text-primary">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground">Visit Us in Person</h4>
                      <p className="text-sm text-muted-foreground">{locationInfo.unitNumber}, Sunway Geo Mall</p>
                      <p className="text-sm text-muted-foreground">{locationInfo.landmark}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col gap-4">
                <h3 className="text-2xl font-bold">Modern. Clean. Friendly.</h3>
                <p className="text-muted-foreground">
                  Our pink-themed restaurant at Sunway Geo Mall is designed for comfort and great memories. Whether it's a quick lunch or a family dinner, we provide a clean and welcoming space for all Dakgalbi lovers.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer Branding Overlay */}
      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
          <div>
            <p className="text-lg font-bold text-primary">Bagus Chicken Dakgalbi</p>
            <p className="text-sm text-muted-foreground">© 2026 Bagus Chicken. All rights reserved.</p>
          </div>
          <div className="flex gap-8 text-sm font-medium text-muted-foreground">
            <Link to={ROUTE_PATHS.HOME} className="hover:text-primary transition-colors">Home</Link>
            <Link to={ROUTE_PATHS.MENU} className="hover:text-primary transition-colors">Menu</Link>
            <Link to={ROUTE_PATHS.ABOUT} className="hover:text-primary transition-colors">Our Story</Link>
            <Link to={ROUTE_PATHS.LOCATION} className="hover:text-primary transition-colors">Location</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
